import moment, { MomentInput } from "moment";
import { Subscription } from "rxjs";
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { CONSTANT } from "src/app/constant";
import { CandidateRole } from "src/app/models/enum";
import { isEts1SessionV2 } from "src/app/cbta-web/ets1/session/ets1-session-form.utils";
import { Ets1SessionColumnVM } from "src/app/cbta-web/ets1/session/ets1-session-layout.utils";
import { extractEts1SessionConfigurationsFromColumns } from "src/app/cbta-web/ets1/session/ets1-session-selection.utils";
import { normalizeReminderNotesForSubmit } from "src/app/cbta-web/ets1/reminder-notes/ets1-reminder-notes-form.utils";
import { AssessmentConfig, FormDataOptions, TS1FormRequestJSON } from "src/app/models/models";

export type SessionCfgRaw = {
	code: string;
	description: string;
	requiresText?: boolean;
	columnIndex?: number;
	displayOrder?: number;
	parentCode?: string | null;
	selectionMode?: string;
	session_conf_type?: { code: string; description: string };
};

export type Observation = { code: string; description?: string; fixed_maneuver?: string };

const BASE_OB_KEYS = new Set([
	"formType",
	"maneuver",
	"maneuverCode",
	"assessmentId",
	"traineeAssessmentId",
	"candidateId",
	"selectedOBs",
	"safetyOutcome",
	"repeat",
]);

export function makeObsValue(lastAction = CONSTANT.OB_LAST_ACTION.CREATE) {
	return {
		totalCount: 0,
		currentCount: 0,
		preDefined: true,
		lastAction,
		isConditional: false,
	};
}

export function parseFormConfigResponse(payload: unknown): {
	observations: Observation[];
	sessionConfigurations: SessionCfgRaw[];
	maneuvers: Array<{ code: string; description: string; subDescription?: string | null }>;
} {
	const root = (payload as { data?: unknown })?.data ?? payload;
	const data = (root as { data?: unknown })?.data ?? root;
	const cfg = data as {
		observations?: Observation[];
		sessionConfigurations?: SessionCfgRaw[];
		maneuvers?: Array<{ code: string; description: string; subDescription?: string | null }>;
	};

	return {
		observations: cfg?.observations ?? [],
		sessionConfigurations: cfg?.sessionConfigurations ?? [],
		maneuvers: cfg?.maneuvers ?? [],
	};
}

export function isTs1FormType(value: FormDataOptions | string | null | undefined): boolean {
	if (!value) return false;
	const code = typeof value === "string" ? value : value.code;
	return code === CONSTANT.FORM_TYPE.ts_1;
}

export function isEts35FormType(value: FormDataOptions | string | null | undefined): boolean {
	if (!value) return false;
	const code = typeof value === "string" ? value : value.code;
	return code === CONSTANT.FORM_TYPE.ts_3 || code === CONSTANT.FORM_TYPE.ts_5;
}

/** TEM competency slots stored as maneuver rows; excluded from admin maneuver pickers/lists. */
export const TS1_V3_COMPETENCY_MANEUVER_CODES = new Set([
	"kno",
	"pro",
	"com",
	"fpa",
	"fpm",
	"ltw",
	"psd",
	"saw",
	"wlm",
]);

export function isTs1V3CompetencyManeuverCode(code?: string | null): boolean {
	if (!code) return false;
	return TS1_V3_COMPETENCY_MANEUVER_CODES.has(String(code).toLowerCase());
}

export function resolveAssessmentManeuverCode(maneuverType: unknown): string {
	if (!maneuverType) return "";
	if (typeof maneuverType === "string") return maneuverType;
	return String((maneuverType as FormDataOptions).code ?? "");
}

export function filterTs1ManeuverOptions<T extends { code: string }>(options: T[]): T[] {
	return options.filter((option) => !isTs1V3CompetencyManeuverCode(option.code));
}

export type Ts1AssessmentConfigEntry = {
	maneuverType?: string | FormDataOptions | null;
	observations?: Array<{
		code?: string;
		observationType?: string;
		attempt?: number;
		candidateRole?: string;
		isConditional?: boolean;
	}>;
	assessmentId?: string;
	sequenceNumber?: number;
};

export function splitTs1AssessmentConfig(assess: Ts1AssessmentConfigEntry[]): {
	maneuvers: Ts1AssessmentConfigEntry[];
	competencies: Ts1AssessmentConfigEntry[];
} {
	const maneuvers: Ts1AssessmentConfigEntry[] = [];
	const competencies: Ts1AssessmentConfigEntry[] = [];

	for (const entry of assess) {
		const code = resolveAssessmentManeuverCode(entry.maneuverType);
		if (isTs1V3CompetencyManeuverCode(code)) {
			competencies.push(entry);
		} else {
			maneuvers.push(entry);
		}
	}

	return { maneuvers, competencies };
}

export function appendTs1CompetencyAssessments(
	maneuverConfig: AssessmentConfig[],
	preservedCompetencies: Ts1AssessmentConfigEntry[],
): AssessmentConfig[] {
	if (!preservedCompetencies.length) return maneuverConfig;

	const competencyConfig = preservedCompetencies.map((entry, idx) => {
		const maneuverType = resolveAssessmentManeuverCode(entry.maneuverType);
		return {
			maneuverType,
			observations: [],
			assessmentId: entry.assessmentId,
			sequenceNumber: maneuverConfig.length + idx + 1,
		};
	});

	return [...maneuverConfig, ...competencyConfig];
}

export type SessionCfgItem = { code: string; description: string };

export const TS1_SESSION_REMARK_MAX_LENGTH = 100;
export const TS1_REMINDER_NOTES_MAX_LENGTH = 1000;

export function syncTs1SessionRemarkControls(
	fb: UntypedFormBuilder,
	sessionRemarks: UntypedFormGroup,
	sessionCodes: string[],
): void {
	const stale = new Set(Object.keys(sessionRemarks.controls));
	for (const code of sessionCodes) {
		if (!sessionRemarks.get(code)) {
			sessionRemarks.addControl(code, fb.control(""));
		}
		stale.delete(code);
	}
	for (const code of stale) {
		sessionRemarks.removeControl(code);
	}
}

export function wireTs1SessionCheckboxRemarkClearing(
	sessions: UntypedFormGroup,
	sessionRemarks: UntypedFormGroup,
	subs: Subscription[],
): void {
	for (const code of Object.keys(sessions.controls)) {
		const c = sessions.get(code);
		if (!c) continue;
		const sub = c.valueChanges.subscribe((checked: boolean) => {
			if (!checked) {
				sessionRemarks.get(code)?.setValue("", { emitEvent: false });
			}
		});
		subs.push(sub);
	}
}

export function wireTs1ReminderNotesClearing(
	reminderNotesEnabled: UntypedFormGroup | UntypedFormControl,
	reminderNotesTemplate: UntypedFormControl,
	subs: Subscription[],
): void {
	const enabledCtrl =
		reminderNotesEnabled instanceof UntypedFormGroup
			? reminderNotesEnabled.get("reminderNotesEnabled")
			: reminderNotesEnabled;
	if (!enabledCtrl) return;

	const sub = enabledCtrl.valueChanges.subscribe((checked: boolean) => {
		if (!checked) {
			reminderNotesTemplate.setValue("", { emitEvent: false });
		}
	});
	subs.push(sub);
}

export function patchTs1FormConfigFromResponse(
	reminderNotesEnabled: UntypedFormControl,
	reminderNotesTemplate: UntypedFormControl,
	resp: {
		reminderNotesEnabled?: boolean;
		reminderNotesTemplate?: string;
		reminder_notes_enabled?: boolean;
		reminder_notes_template?: string | null;
	},
): void {
	const enabled = resp.reminderNotesEnabled ?? resp.reminder_notes_enabled ?? false;
	const rawTemplate = resp.reminderNotesTemplate ?? resp.reminder_notes_template ?? "";
	const template = String(rawTemplate).trim().slice(0, TS1_REMINDER_NOTES_MAX_LENGTH);
	reminderNotesEnabled.setValue(!!enabled);
	reminderNotesTemplate.setValue(template);
}

/** Normalize GET /form/:id payloads (direct body or `{ data: ... }` wrapper). */
export function unwrapFormApiResponse<T>(resp: T | { data?: T | null }): T {
	if (resp != null && typeof resp === "object" && "data" in resp && (resp as { data?: T | null }).data != null) {
		return (resp as { data: T }).data;
	}
	return resp as T;
}

export function buildAdminFormSessionPayload(
	sessions: Record<string, boolean>,
	sessionRemarks: Record<string, string>,
	columns: Ets1SessionColumnVM[],
): { sessionDetails: string[]; sessionRemarksByCode?: Record<string, string> } {
	const configs = extractEts1SessionConfigurationsFromColumns(columns, sessions, sessionRemarks);
	const sessionRemarksByCode: Record<string, string> = {};

	for (const config of configs) {
		const code = config.code?.trim();
		if (!code) continue;
		const remark = config.selectionRemarks?.trim();
		if (remark) {
			sessionRemarksByCode[code] = remark.slice(0, TS1_SESSION_REMARK_MAX_LENGTH);
		}
	}

	return {
		sessionDetails: configs.map((config) => config.code).filter((code): code is string => !!code),
		...(Object.keys(sessionRemarksByCode).length ? { sessionRemarksByCode } : {}),
	};
}

export function buildSessionRemarksByCode(
	sessions: Record<string, boolean>,
	sessionRemarks: Record<string, string>,
): Record<string, string> {
	const out: Record<string, string> = {};
	for (const [code, selected] of Object.entries(sessions ?? {})) {
		if (!selected) continue;
		const remark = (sessionRemarks?.[code] ?? "").toString().trim();
		if (remark) {
			out[code] = remark.slice(0, TS1_SESSION_REMARK_MAX_LENGTH);
		}
	}
	return out;
}

export function patchTs1SessionRemarksFromResponse(
	sessionRemarks: UntypedFormGroup,
	sessionDetails: Array<{ code: string; selectionRemarks?: string }>,
): void {
	for (const s of sessionDetails ?? []) {
		const ctrl = sessionRemarks.get(s.code);
		if (ctrl && s.selectionRemarks?.trim()) {
			ctrl.setValue(s.selectionRemarks.trim().slice(0, TS1_SESSION_REMARK_MAX_LENGTH), { emitEvent: false });
		}
	}
}

export function sortSessionsAlphabetically(sessions: SessionCfgRaw[]): SessionCfgItem[] {
	return [...sessions]
		.map((s) => ({ code: s.code, description: s.description }))
		.sort((a, b) => a.description.localeCompare(b.description, undefined, { sensitivity: "base" }));
}

export function splitSessionsIntoColumns<T>(items: T[], columns: number): T[][] {
	if (columns <= 0) return [];
	const cols: T[][] = Array.from({ length: columns }, () => []);
	const perCol = Math.ceil(items.length / columns);

	items.forEach((item, idx) => {
		const colIdx = Math.min(Math.floor(idx / perCol), columns - 1);
		cols[colIdx].push(item);
	});

	return cols;
}

export function buildTs1SessionColumns(sessions: SessionCfgRaw[]): SessionCfgItem[][] {
	return splitSessionsIntoColumns(sortSessionsAlphabetically(sessions), 4);
}

export function buildManeuverV2FromObservations(
	fb: UntypedFormBuilder,
	observations: Observation[],
	formType: string | FormDataOptions,
	lastAction = CONSTANT.OB_LAST_ACTION.CREATE,
): UntypedFormGroup {
	const byCategory = new Map<string, Array<{ key: string; num: number }>>();
	const seen = new Set<string>();

	for (const o of observations || []) {
		const code = (o.code || "").trim();
		if (!code || seen.has(code)) continue;
		seen.add(code);

		const m = /^([a-zA-Z]+)_(\d+)$/.exec(code);
		if (!m) continue;
		const cat = m[1].toUpperCase();
		const num = Number(m[2]);
		const key = `${cat}${num}`;
		if (!byCategory.has(cat)) byCategory.set(cat, []);
		byCategory.get(cat)!.push({ key, num });
	}

	const categoryGroups: Record<string, UntypedFormGroup> = {};
	for (const [cat, items] of byCategory.entries()) {
		items.sort((a, b) => a.num - b.num);
		const controls: Record<string, UntypedFormGroup> = {};
		for (const it of items) {
			controls[it.key] = fb.group({
				pf: [{ value: makeObsValue(lastAction), disabled: false }],
				pm: [{ value: makeObsValue(lastAction), disabled: false }],
			});
		}
		categoryGroups[cat] = fb.group(controls);
	}

	return fb.group({
		maneuver: [""],
		formType: [formType],
		maneuverCode: [""],
		assessmentId: [""],
		traineeAssessmentId: [""],
		candidateId: [""],
		selectedOBs: [[]],
		safetyOutcome: [-2],
		repeat: [0],
		...categoryGroups,
	});
}

export function patchAttemptsIntoManeuverV2(
	mfg: UntypedFormGroup,
	obs: Array<{
		code?: string;
		observationType?: string;
		attempt: number;
		candidateRole?: string;
		isConditional?: boolean;
	}>,
) {
	for (const o of obs || []) {
		const raw = (o.code ?? o.observationType ?? "").trim();
		const m = /^([a-zA-Z]+)_(\d+)$/.exec(raw);
		if (!m) continue;

		const cat = m[1].toUpperCase();
		const key = `${cat}${Number(m[2])}`;
		const roleKey = o.candidateRole === CandidateRole.PF ? "pf" : o.candidateRole === CandidateRole.PM ? "pm" : null;
		if (!roleKey) continue;

		const obGroup = (mfg.get(cat) as UntypedFormGroup | null)?.get(key) as UntypedFormGroup | null;
		const control = obGroup?.get(roleKey);
		if (!control) continue;

		const isConditional = o.isConditional === true;
		const attempt = isConditional ? 0 : o.attempt;
		const cur = control.value || makeObsValue(CONSTANT.OB_LAST_ACTION.EDIT);
		control.patchValue(
			{
				...cur,
				totalCount: attempt,
				currentCount: attempt,
				preDefined: true,
				isConditional,
				lastAction: CONSTANT.OB_LAST_ACTION.EDIT,
			},
			{ emitEvent: false },
		);
	}
}

export function buildV2RequestPayload(
	formValue: Record<string, unknown>,
	aocCode: string,
	dateFormat: string,
	momentFn: typeof moment,
	ets1SessionColumns: Ets1SessionColumnVM[] = [],
): TS1FormRequestJSON {
	const sessions = formValue["sessions"] as Record<string, boolean>;
	const sessionRemarks = (formValue["sessionRemarks"] as Record<string, string>) ?? {};
	const sessionPayload = ets1SessionColumns.length
		? buildAdminFormSessionPayload(sessions ?? {}, sessionRemarks, ets1SessionColumns)
		: {
				sessionDetails: Object.entries(sessions ?? {})
					.filter(([code, selected]) => code !== "not_required" && selected)
					.map(([code]) => code),
				sessionRemarksByCode: buildSessionRemarksByCode(sessions ?? {}, sessionRemarks),
			};
	const sessionCodes = sessionPayload.sessionDetails;

	const maneuvers = Array.isArray(formValue["maneuvers"]) ? formValue["maneuvers"] : [];
	const assessmentConfig = maneuvers.reduce((acc: AssessmentConfig[], mv: Record<string, unknown>) => {
		const selectedOBs: AssessmentConfig["observations"] = [];

		Object.keys(mv ?? {}).forEach((catKey) => {
			if (BASE_OB_KEYS.has(catKey)) return;
			const grp = (mv[catKey] ?? {}) as Record<
				string,
				{ pf?: { currentCount?: number; isConditional?: boolean }; pm?: { currentCount?: number; isConditional?: boolean } }
			>;
			Object.keys(grp).forEach((obKey) => {
				const cell = grp[obKey];
				const m = /^([A-Z]+)(\d+)$/.exec(obKey);
				if (!m) return;
				const observationType = `${m[1].toLowerCase()}_${m[2]}`;

				const pf = cell?.pf;
				if ((pf?.currentCount ?? 0) > 0 || pf?.isConditional === true) {
					selectedOBs.push({
						observationType,
						attempt: pf?.currentCount ?? 0,
						candidateRole: CandidateRole.PF,
						isConditional: pf?.isConditional === true,
					});
				}
				const pm = cell?.pm;
				if ((pm?.currentCount ?? 0) > 0 || pm?.isConditional === true) {
					selectedOBs.push({
						observationType,
						attempt: pm?.currentCount ?? 0,
						candidateRole: CandidateRole.PM,
						isConditional: pm?.isConditional === true,
					});
				}
			});
		});

		const maneuverVal = mv?.["maneuver"];
		const maneuverType =
			typeof maneuverVal === "string" ? maneuverVal : (maneuverVal as FormDataOptions | null)?.code;
		if (maneuverType) {
			acc.push({ maneuverType, observations: selectedOBs });
		}
		return acc;
	}, []);

	const sessionRemarksByCode = sessionPayload.sessionRemarksByCode ?? {};

	const ets1SessionVersion = Number(formValue["ets1SessionVersion"] ?? 1);
	const reminderNotesEnabled = !!formValue["reminderNotesEnabled"];
	const reminderNotesTemplate = String(formValue["reminderNotesTemplate"] ?? "")
		.trim()
		.slice(0, TS1_REMINDER_NOTES_MAX_LENGTH);
	const reminderNotes = normalizeReminderNotesForSubmit(
		Array.isArray(formValue["reminderNotes"]) ? formValue["reminderNotes"] : [],
	);
	return {
		name: formValue["formName"] as string,
		startDate: momentFn(formValue["syllabusStartDate"] as MomentInput).format(dateFormat),
		endDate: momentFn(formValue["syllabusEndDate"] as MomentInput).format(dateFormat),
		formType: (formValue["formType"] as FormDataOptions).code,
		configVersion: 2,
		ets1SessionVersion,
		sessionDetails: sessionCodes,
		...(Object.keys(sessionRemarksByCode).length > 0 ? { sessionRemarksByCode } : {}),
		...(isEts1SessionV2({ ets1SessionVersion })
			? { reminderNotes }
			: reminderNotesEnabled
				? {
						reminderNotesEnabled: true,
						reminderNotesTemplate,
					}
				: {}),
		assessmentConfig,
		aocCode,
	};
}
